import {useState, useEffect} from "react"
import SimpleCoordinate from "./SimpleCoordinate"
import LandController from "./LandController"

export interface UseBoundariesResult {
    boundaryMap: Record<number, SimpleCoordinate[]>
    allCoordinates: SimpleCoordinate[]
    loading: boolean
    error: boolean
}

/**
 * Hook for fetching boundary coordinates for multiple lands.
 * Returns individual boundaries keyed by landId and a combined array.
 */
export function useBoundaries(landIds: number[] | undefined): UseBoundariesResult {
    const [boundaryMap, setBoundaryMap] = useState<Record<number, SimpleCoordinate[]>>({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    // Stable key for dependency comparison
    const key = landIds?.slice().sort().join(',') ?? ''

    useEffect(() => {
        if (!landIds || landIds.length === 0) {
            setBoundaryMap({})
            setLoading(false)
            setError(false)
            return
        }

        let cancelled = false
        setLoading(true)
        setError(false)

        LandController.boundaries(landIds)
            .then(result => {
                if (!cancelled) {
                    setBoundaryMap(result)
                    setLoading(false)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setBoundaryMap({})
                    setError(true)
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [key])

    const allCoordinates: SimpleCoordinate[] = []
    for (const coords of Object.values(boundaryMap)) {
        allCoordinates.push(...coords)
    }

    return {boundaryMap, allCoordinates, loading, error}
}
